import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header.tsx';
import Sidebar from './Sidebar.tsx';
import styles from './PadData.module.css';
import useTheme from '../hooks/useTheme.ts';
import axios from 'axios';

interface Pad {
  id: number;
  pad_name: string;
  fleet: number;
  status: string;
  job_number: number;
  organization_id: number;
  stage_count: number;
  well_count: number;
  start_ts: number | null;
  end_ts: number | null;
}

interface Organization {
  id: number;
  name: string;
}

const PAD_STATUSES = ['Planned', 'Active', 'Completed'] as const;
type PadStatus = typeof PAD_STATUSES[number];

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Active:    { bg: 'rgba(34,197,94,0.15)',  text: '#16a34a', border: 'rgba(34,197,94,0.4)'  },
  Completed: { bg: 'rgba(99,102,241,0.15)', text: '#6366f1', border: 'rgba(99,102,241,0.4)' },
  Planned:   { bg: 'rgba(249,196,48,0.15)', text: '#b45309', border: 'rgba(249,196,48,0.4)' },
};

function StatusBadge({
  status,
  editable,
  onStatusChange,
}: {
  status: string;
  editable: boolean;
  onStatusChange: (newStatus: PadStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; flipUp: boolean }>({ top: 0, left: 0, flipUp: false });
  const ref = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const colors = STATUS_COLORS[status] ?? { bg: 'rgba(156,163,175,0.15)', text: '#6b7280', border: 'rgba(156,163,175,0.4)' };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = (e: React.MouseEvent) => {
    if (!editable) return;
    e.stopPropagation();
    if (!open && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const dropdownHeight = PAD_STATUSES.length * 37;
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < dropdownHeight + 12;
      setDropdownPos({
        top: flipUp ? rect.top - dropdownHeight - 6 : rect.bottom + 6,
        left: rect.left,
        flipUp,
      });
    }
    setOpen(!open);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <span
        ref={badgeRef}
        onClick={handleOpen}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 10px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.02em',
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          cursor: editable ? 'pointer' : 'default',
          userSelect: 'none',
          transition: 'opacity 0.15s',
        }}
      >
        {status}
        {editable && <span style={{ fontSize: 9, marginLeft: 2 }}>▼</span>}
      </span>

      {open && (
        <div
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 9999,
            background: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            minWidth: 130,
            overflow: 'hidden',
          }}
        >
          {PAD_STATUSES.map((s) => {
            const c = STATUS_COLORS[s];
            return (
              <div
                key={s}
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(s);
                  setOpen(false);
                }}
                style={{
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: s === status ? 600 : 400,
                  color: c.text,
                  backgroundColor: s === status ? c.bg : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = c.bg)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = s === status ? c.bg : 'transparent')}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  backgroundColor: c.text, display: 'inline-block', flexShrink: 0,
                }} />
                {s}
                {s === status && <span style={{ marginLeft: 'auto', fontSize: 11 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PadData() {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(true);
  const [pads, setPads] = useState<Pad[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Pad>('fleet');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<string>('Active');
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const { theme } = useTheme();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const isManager = (JSON.parse(localStorage.getItem('roles') || '[]') as string[]).includes('Manager');
  const isDark = theme === 'dark';

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [padsRes, orgsRes] = await Promise.all([
          axios.get(`${backendUrl}/pads`, { headers: getAuthHeaders() }),
          axios.get(`${backendUrl}/organizations/list`, { headers: getAuthHeaders() }),
        ]);
        setPads(padsRes.data);
        setOrganizations(orgsRes.data);
      } catch (err) {
        setError('Failed to fetch pad data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [backendUrl, getAuthHeaders]);

  const getOrgName = (orgId: number) =>
    organizations.find((o) => o.id === orgId)?.name ?? '—';

  const handleStatusChange = async (padId: number, newStatus: PadStatus) => {
    setUpdatingId(padId);
    try {
      await axios.put(
        `${backendUrl}/pads/${padId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      setPads((prev) => prev.map((p) => p.id === padId ? { ...p, status: newStatus } : p));
    } catch {
      setError('Failed to update pad status.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSort = (field: keyof Pad) => {
    if (field === sortField) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Status counts for filter pills
  const statusCounts = pads.reduce<Record<string, number>>((acc, p) => {
    const s = p.status || 'Planned';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const filterOptions = ['All', ...PAD_STATUSES];

  const filteredPads = pads
    .filter((p) => {
      const matchSearch = p.pad_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === 'All' || (p.status || 'Planned') === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && typeof bv === 'string')
        return sortDirection === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDirection === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });

  const SortIcon = ({ field }: { field: keyof Pad }) =>
    sortField === field ? (
      <span style={{ marginLeft: 4, fontSize: 10, color: '#F9C430' }}>
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    ) : (
      <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.3 }}>▲▼</span>
    );

  // Shared inline styles
  const thStyle: React.CSSProperties = {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: isDark ? '#9CA3AF' : '#6B7280',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${isDark ? '#2D2D2D' : '#E5E7EB'}`,
  };

  const tdStyle: React.CSSProperties = {
    padding: '14px 16px',
    fontSize: 14,
    color: 'var(--color-text)',
    borderBottom: `1px solid ${isDark ? '#1F1F1F' : '#F3F4F6'}`,
  };

  return (
    <section className={`${styles.section} ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      <div className={styles.profile}>
        <Header title="Pad Data" setShowSidebar={setShowSidebar} showSidebar={showSidebar} />

        <div className={styles.container}>
          <div
            className={styles.sidebarStyle}
            style={{ width: showSidebar ? '240px' : '0', transition: 'width 0.3s' }}
          >
            <Sidebar />
          </div>

          <div className={styles.mainContentStyle}>

            {/* ── Top bar ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              {/* Search */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: isDark ? '#1A1A1A' : '#F9FAFB',
                border: `1px solid ${isDark ? '#2D2D2D' : '#E5E7EB'}`,
                borderRadius: 8, padding: '8px 12px', flex: 1, minWidth: 200,
              }}>
                <span style={{ color: '#6B7280', fontSize: 14 }}>🔍</span>
                <input
                  type="text"
                  placeholder="Search pads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    border: 'none', background: 'transparent', outline: 'none',
                    fontSize: 14, color: 'var(--color-text)', width: '100%',
                  }}
                />
                {searchTerm && (
                  <span
                    onClick={() => setSearchTerm('')}
                    style={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 16, lineHeight: 1 }}
                  >×</span>
                )}
              </div>

              {/* Status filter pills */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {filterOptions.map((s) => {
                  const isActive = statusFilter === s;
                  const c = s !== 'All' ? STATUS_COLORS[s] : null;
                  const count = s === 'All' ? pads.length : (statusCounts[s] || 0);
                  return (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: isActive
                          ? `1.5px solid ${c?.border ?? '#F9C430'}`
                          : `1px solid ${isDark ? '#2D2D2D' : '#E5E7EB'}`,
                        background: isActive
                          ? (c?.bg ?? 'rgba(249,196,48,0.15)')
                          : (isDark ? '#1A1A1A' : '#F9FAFB'),
                        color: isActive ? (c?.text ?? '#b45309') : (isDark ? '#9CA3AF' : '#6B7280'),
                        transition: 'all 0.15s',
                      }}
                    >
                      {s} <span style={{ opacity: 0.7 }}>({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {error && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 16,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                color: '#EF4444', fontSize: 13,
              }}>
                {error}
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: isDark ? '#6B7280' : '#9CA3AF' }}>
                Loading pad data...
              </div>
            ) : (
              <div style={{
                background: isDark ? '#141414' : '#FFFFFF',
                border: `1px solid ${isDark ? '#2D2D2D' : '#E5E7EB'}`,
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: isDark ? '#1A1A1A' : '#F9FAFB' }}>
                    <tr>
                      {[
                        { label: 'Pad Name',    field: 'pad_name'   },
                        { label: 'Customer',    field: null          },
                        { label: 'Fleet',       field: 'fleet'       },
                        { label: 'Status',      field: 'status'      },
                        { label: 'Job #',       field: 'job_number'  },
                        { label: 'Wells',       field: 'well_count'  },
                        { label: 'Stages',      field: 'stage_count' },
                      ].map(({ label, field }) => (
                        <th
                          key={label}
                          style={thStyle}
                          onClick={() => field && handleSort(field as keyof Pad)}
                        >
                          {label}
                          {field && <SortIcon field={field as keyof Pad} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPads.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', padding: 48, color: '#6B7280' }}>
                          No pads match your filters.
                        </td>
                      </tr>
                    ) : (
                      filteredPads.map((pad) => (
                        <tr
                          key={pad.id}
                          onClick={() => navigate(`/treatment-graph/${pad.id}`)}
                          style={{ cursor: 'pointer', transition: 'background 0.12s' }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLTableRowElement).style.background = isDark ? '#1A1A1A' : '#F9FAFB';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLTableRowElement).style.background = 'transparent';
                          }}
                        >
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{pad.pad_name}</td>
                          <td style={{ ...tdStyle, color: isDark ? '#9CA3AF' : '#6B7280' }}>
                            {getOrgName(pad.organization_id)}
                          </td>
                          <td style={tdStyle}>{pad.fleet ?? '—'}</td>
                          <td style={{ ...tdStyle, position: 'relative' }}>
                            {updatingId === pad.id ? (
                              <span style={{ fontSize: 12, color: '#6B7280' }}>Saving...</span>
                            ) : (
                              <StatusBadge
                                status={pad.status || 'Planned'}
                                editable={isManager}
                                onStatusChange={(s) => handleStatusChange(pad.id, s)}
                              />
                            )}
                          </td>
                          <td style={{ ...tdStyle, color: isDark ? '#9CA3AF' : '#6B7280' }}>{pad.job_number}</td>
                          <td style={tdStyle}>{pad.well_count ?? 0}</td>
                          <td style={tdStyle}>{pad.stage_count ?? 0}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Footer count */}
                <div style={{
                  padding: '10px 16px',
                  borderTop: `1px solid ${isDark ? '#2D2D2D' : '#F3F4F6'}`,
                  fontSize: 12,
                  color: isDark ? '#6B7280' : '#9CA3AF',
                  background: isDark ? '#1A1A1A' : '#F9FAFB',
                }}>
                  Showing {filteredPads.length} of {pads.length} pads
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default PadData;